import { useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import parse from 'html-react-parser'
import { AlertTriangle, Megaphone, X } from 'lucide-react'

import { htmlParseOptions } from '../../utils/utils'
import './broadcastBar.css'

/**
 * One announcement, as a single 40px row (CAR-4102): icon, bold subject and the message on one line (two on
 * a phone), cut off with an ellipsis. "More" shows only when the text is cut off, and opens it in place.
 *
 * Info is a navy strip that matches the top bar; Critical is the error red, with a warning icon. Only the text
 * is a live region (status / alert), so the buttons are not read out with it; the icon is decorative.
 * Used by the live BroadcastBar and by Admin → Broadcast Message → Preview Message.
 */
export const BroadcastBanner = ({ type, subject, message, onDismiss }) => {
  const isCritical = type === 'CRITICAL'
  const textRef = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [truncated, setTruncated] = useState(false)

  // "More" only when the line is actually cut off - measured, and re-measured when the row resizes.
  useLayoutEffect(() => {
    const el = textRef.current
    if (!el || expanded) return undefined
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [expanded, subject, message])

  const tone = isCritical
    ? {
        row: 'bg-error-light border border-error/30 border-l-[3px] border-l-error text-foreground',
        icon: 'bg-white text-error',
        message: 'text-foreground/80',
        action: 'text-foreground underline underline-offset-2 hover:text-error',
        close: 'text-foreground/70 hover:bg-foreground/5'
      }
    : {
        row: 'bg-gradient-to-r from-sidebar to-sidebar-end text-white',
        icon: 'bg-primary/25 text-blue-300',
        message: 'text-white/80',
        action: 'text-blue-300 hover:text-white',
        close: 'text-white/75 hover:bg-white/10'
      }
  const Icon = isCritical ? AlertTriangle : Megaphone

  return (
    <div
      data-testid='broadcast-banner'
      data-type={isCritical ? 'CRITICAL' : 'INFO'}
      className={`flex min-h-10 items-start gap-2.5 rounded-card py-1.5 pl-2 pr-1.5 ${tone.row}`}
    >
      <span aria-hidden='true' className={`mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-md ${tone.icon}`}>
        <Icon className='h-4 w-4' />
      </span>
      <div
        ref={textRef}
        role={isCritical ? 'alert' : 'status'}
        className={`broadcast-line my-1 min-w-0 flex-1 text-[13px] leading-5 ${
          expanded ? 'broadcast-line--expanded' : 'line-clamp-2 min-[435px]:line-clamp-1'
        }`}
      >
        {subject && <span className='broadcast-subject font-semibold'>{subject}</span>}
        {subject && ' '}
        <span className={`broadcast-message ${tone.message}`}>{parse(message || '', htmlParseOptions)}</span>
      </div>
      {(truncated || expanded) && (
        <button
          type='button'
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          className={`mt-1 h-6 flex-none whitespace-nowrap rounded px-1.5 text-[13px] font-semibold ${tone.action}`}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      )}
      {onDismiss && (
        <button
          type='button'
          aria-label='Dismiss announcement'
          onClick={onDismiss}
          className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-md ${tone.close}`}
        >
          <X className='h-4 w-4' />
        </button>
      )}
    </div>
  )
}

BroadcastBanner.propTypes = {
  type: PropTypes.string,
  subject: PropTypes.string,
  message: PropTypes.string,
  onDismiss: PropTypes.func
}
